/**
 * DAC-07 navigation interactions (baked-in A/B winner, native rebuild).
 *
 * Operates on the server-rendered markup from snippets/dac-nav.liquid and
 * dac-country-selector.liquid. Unlike the GrowthBook experiment script, this
 * builds NO DOM and holds NO navData — it only wires behavior:
 *   - desktop dropdown / flyout open-close + positioning
 *   - mobile drill-down panel stack (+ reset on drawer close)
 *   - the mobile country selector toggle / submit
 *   - megamenu_click dataLayer analytics (4 fields, matching the theme)
 *
 * Loaded with `defer` from snippets/header-V2.liquid.
 */
(function () {
  "use strict";
  if (window.__dacNavInit) return;
  window.__dacNavInit = true;

  var OPEN_DELAY = 200;
  var CLOSE_DELAY = 150;

  /* ------------------------------- Analytics ------------------------------ */

  function pushMegamenuClick(categoryName, link) {
    var labelEl = link.querySelector(".dac-label");
    var elementName =
      (labelEl && labelEl.textContent.trim()) ||
      link.getAttribute("title") ||
      (link.textContent || "").trim().split("\n")[0];
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "megamenu_click",
      megamenu_category_name: categoryName || "",
      megamenu_element_name: elementName || "",
      megamenu_element_type: link.querySelector("img") ? "image" : "text",
    });
  }

  /* -------------------------------- Desktop ------------------------------- */

  function closeFlyouts(scope, except) {
    scope.querySelectorAll(".dac-nav__subitem.is-open").forEach(function (subitem) {
      if (subitem === except) return;
      var subtrigger = subitem.querySelector(":scope > .dac-nav__subtrigger");
      var flyout = subitem.querySelector(":scope > .dac-nav__flyout");
      if (subtrigger) subtrigger.setAttribute("aria-expanded", "false");
      if (flyout) flyout.hidden = true;
      subitem.classList.remove("is-open");
    });
  }

  function closeAllDropdowns(nav, except) {
    nav.querySelectorAll(".dac-nav__item.is-open").forEach(function (item) {
      if (item === except) return;
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      var dropdown = item.querySelector(":scope > .dac-nav__dropdown");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (dropdown) dropdown.hidden = true;
      item.classList.remove("is-open");
      closeFlyouts(item);
    });
  }

  function bindDesktopEvents(nav) {
    var closeTimer = null;
    var openTimer = null;
    // Once a dropdown is opened by click it stays pinned: hover in/out is
    // ignored until it is closed by a second click, Escape or a click outside.
    var clickMode = false;
    var clearOpenTimer = function () {
      window.clearTimeout(openTimer);
      openTimer = null;
    };
    var clearCloseTimer = function () {
      window.clearTimeout(closeTimer);
      closeTimer = null;
    };

    var openDropdown = function (item) {
      closeAllDropdowns(nav, item);
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      var dropdown = item.querySelector(":scope > .dac-nav__dropdown");
      if (!trigger || !dropdown) return;
      trigger.setAttribute("aria-expanded", "true");
      // Full-width and centered dropdowns float below the sticky header.
      if (
        dropdown.classList.contains("dac-nav__dropdown--mega") ||
        dropdown.classList.contains("dac-nav__dropdown--grouped")
      ) {
        var header = document.querySelector("header.header-dac");
        var top = header ? header.getBoundingClientRect().bottom : 0;
        dropdown.style.top = Math.max(top, 0) + "px";
      }
      dropdown.hidden = false;
      item.classList.add("is-open");
    };

    var scheduleOpenDropdown = function (item) {
      clearOpenTimer();
      openTimer = window.setTimeout(function () {
        openTimer = null;
        openDropdown(item);
      }, OPEN_DELAY);
    };

    var closeDropdown = function (item) {
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      var dropdown = item.querySelector(":scope > .dac-nav__dropdown");
      if (trigger) trigger.setAttribute("aria-expanded", "false");
      if (dropdown) dropdown.hidden = true;
      item.classList.remove("is-open");
      closeFlyouts(item);
      clickMode = false;
    };

    nav.querySelectorAll(".dac-nav__item[data-has-children]").forEach(function (item) {
      item.addEventListener("mouseenter", function () {
        if (clickMode) return;
        clearCloseTimer();
        scheduleOpenDropdown(item);
      });
      item.addEventListener("mouseleave", function () {
        if (clickMode || item.contains(document.activeElement)) return;
        clearOpenTimer();
        closeTimer = window.setTimeout(function () {
          closeDropdown(item);
        }, CLOSE_DELAY);
      });
      var trigger = item.querySelector(":scope > .dac-nav__trigger");
      if (trigger) {
        trigger.addEventListener("click", function (event) {
          event.preventDefault();
          clearOpenTimer();
          clearCloseTimer();
          // Clicking a dropdown that hover already opened pins it rather than
          // closing it; only a click while pinned toggles it back shut.
          if (clickMode && item.classList.contains("is-open")) {
            closeDropdown(item);
          } else {
            openDropdown(item);
            clickMode = true;
          }
        });
      }
    });

    nav.querySelectorAll(".dac-nav__subitem[data-has-children]").forEach(function (subitem, index) {
      var subtrigger = subitem.querySelector(":scope > .dac-nav__subtrigger");
      var flyout = subitem.querySelector(":scope > .dac-nav__flyout");
      if (!subtrigger || !flyout) return;
      var pinned = false;
      if (!flyout.id) flyout.id = "dac-nav-flyout-" + index;
      subtrigger.setAttribute("aria-controls", flyout.id);
      var openFlyout = function () {
        closeFlyouts(nav, subitem);
        subtrigger.setAttribute("aria-expanded", "true");
        flyout.hidden = false;
        var groupedPanel = subitem.closest(".dac-nav__dropdown--grouped");
        if (groupedPanel) {
          var rect = groupedPanel.getBoundingClientRect();
          flyout.style.position = "fixed";
          flyout.style.top = rect.top + "px";
          flyout.style.left = rect.right + "px";
        }
        subitem.classList.add("is-open");
      };
      subtrigger.addEventListener("click", function (event) {
        event.preventDefault();
        clearOpenTimer(); clearCloseTimer();
        if (pinned && subitem.classList.contains("is-open")) {
          closeFlyouts(nav); pinned = false;
        } else {
          openFlyout(); pinned = true; clickMode = true;
        }
      });
      subtrigger.addEventListener("keydown", function (event) {
        if (event.key !== "ArrowDown") return;
        event.preventDefault(); openFlyout(); pinned = true; clickMode = true;
        flyout.querySelector("a[href],button")?.focus({ preventScroll: true });
      });
      subitem.addEventListener("mouseenter", openFlyout);
      subitem.addEventListener("mouseleave", function () {
        if ((pinned && subitem.classList.contains("is-open")) || subitem.contains(document.activeElement)) return;
        subtrigger.setAttribute("aria-expanded", "false"); flyout.hidden = true;
        subitem.classList.remove("is-open");
      });
    });

    nav.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      const subitem = event.target.closest(".dac-nav__subitem.is-open");
      const item = event.target.closest(".dac-nav__item.is-open");
      if (!subitem && !item) return;
      event.preventDefault(); event.stopPropagation();
      clearOpenTimer(); clearCloseTimer();
      if (subitem) {
        closeFlyouts(nav);
        subitem.querySelector(":scope > .dac-nav__subtrigger")?.focus({ preventScroll: true });
      } else {
        closeDropdown(item);
        item.querySelector(":scope > .dac-nav__trigger")?.focus({ preventScroll: true });
      }
    });
    nav.addEventListener("focusout", function () {
      setTimeout(function () {
        if (nav.contains(document.activeElement)) return;
        clearOpenTimer(); clearCloseTimer(); clickMode = false;
        closeAllDropdowns(nav);
      }, 0);
    });

    // Analytics: any link click inside the desktop nav.
    nav.addEventListener("click", function (event) {
      var link = event.target.closest("a[href]");
      if (!link || !nav.contains(link)) return;
      var openItem = link.closest(".dac-nav__item");
      var catEl = openItem && openItem.querySelector(":scope > .dac-nav__trigger .dac-label");
      pushMegamenuClick(catEl ? catEl.textContent.trim() : "", link);
    });

    document.addEventListener("keydown", function (event) {
      if (event.key !== "Escape") return;
      clearOpenTimer();
      clickMode = false;
      closeAllDropdowns(nav);
    });
    document.addEventListener("click", function (event) {
      if (nav.contains(event.target)) return;
      clearOpenTimer();
      clickMode = false;
      closeAllDropdowns(nav);
    });
  }

  /* -------------------------------- Mobile -------------------------------- */

  function bindMobileEvents(root) {
    var stack = ["root"];
    var panelTriggers = [];
    var scrollPositions = {};
    var drawer = root.closest("#drawer_menu-mobile_menu");

    var showPanel = function (id, direction, focusTarget) {
      var currentPanel = root.querySelector(".dac-mnav__panel.is-active");
      if (drawer && currentPanel) scrollPositions[currentPanel.dataset.panel] = drawer.scrollTop;
      root.querySelectorAll(".dac-mnav__panel").forEach(function (panel) {
        var isActive = panel.dataset.panel === id;
        panel.hidden = !isActive;
        panel.classList.toggle("is-active", isActive);
        panel.classList.remove("from-right", "from-left");
        if (isActive) panel.classList.add(direction === "back" ? "from-left" : "from-right");
      });
      root.querySelectorAll(".dac-mnav__trigger[aria-expanded='true']").forEach(function (trigger) {
        if (trigger.dataset.target !== id) trigger.setAttribute("aria-expanded", "false");
      });
      if (drawer) drawer.scrollTop = scrollPositions[id] || 0;
      if (focusTarget !== false) {
        const panel = [...root.querySelectorAll(".dac-mnav__panel")].find(node => node.dataset.panel === id);
        (focusTarget || panel?.querySelector(".dac-mnav__back, a[href], button"))?.focus({ preventScroll: true });
      }
    };

    root.addEventListener("click", function (event) {
      var trigger = event.target.closest(".dac-mnav__trigger");
      if (trigger && root.contains(trigger)) {
        event.preventDefault();
        var target = trigger.dataset.target;
        trigger.setAttribute("aria-expanded", "true");
        stack.push(target);
        panelTriggers.push(trigger);
        showPanel(target, "forward");
        return;
      }
      var back = event.target.closest(".dac-mnav__back");
      if (back && root.contains(back)) {
        event.preventDefault();
        stack.pop();
        var opener = panelTriggers.pop();
        var backTarget = back.dataset.target || stack[stack.length - 1] || "root";
        showPanel(backTarget, "back", opener);
        return;
      }
      // Analytics + navigation for leaf links.
      var link = event.target.closest("a[href]");
      if (link && root.contains(link)) {
        var panel = link.closest(".dac-mnav__panel");
        var titleEl = panel && panel.querySelector(".dac-mnav__back-title");
        event.preventDefault();
        pushMegamenuClick(titleEl ? titleEl.textContent.trim() : "", link);
        var href = link.href;
        window.setTimeout(function () {
          window.location.href = href;
        }, 200);
      }
    });

    root.__dacResetNav = function () {
      stack.length = 0;
      stack.push("root");
      panelTriggers.length = 0;
      root.closest("#drawer_menu-mobile_menu")?.querySelector(".dac-country__form")?.__dacCloseCountry?.(false);
      showPanel("root", "back", false);
      scrollPositions = {};
      if (drawer) drawer.scrollTop = 0;
    };
  }

  /**
   * Reset the mobile drill-down to the root panel whenever the drawer closes,
   * so reopening always starts from the top level.
   */
  function watchDrawerClose(root) {
    var drawer = document.getElementById("drawer_menu-mobile_menu");
    if (!drawer) return;
    var isOpen = function () {
      return drawer.className.indexOf("translate-x-0") !== -1;
    };
    var wasOpen = isOpen();
    var observer = new MutationObserver(function () {
      var open = isOpen();
      if (wasOpen && !open && typeof root.__dacResetNav === "function") {
        root.__dacResetNav();
      }
      wasOpen = open;
    });
    observer.observe(drawer, { attributes: true, attributeFilter: ["class"] });
  }

  /* --------------------------- Country selector --------------------------- */

  function bindCountrySelector(form) {
    var toggle = form.querySelector('.dac-country__toggle');
    var list = form.querySelector('.dac-country__list');
    var codeInput = form.querySelector('input[name="country_code"]');
    var search = form.querySelector('#dac-mobile-country-search');
    var clear = form.querySelector('.dac-country__clear');
    var drawer = form.closest('#drawer_menu-mobile_menu');
    if (!toggle || !list || !codeInput || !search) return;
    var rows;
    var ensureCountryRows = function () {
      if (rows) return;
      var source = document.querySelector('localization-form #country-list-template');
      if (!source) return;
      var options = list.querySelector('.dac-country__options');
      var fragment = document.createDocumentFragment();
      source.content.querySelectorAll('li[data-country]').forEach(function (original) {
        var link = original.querySelector('a[data-value]');
        var row = document.createElement('li');
        row.dataset.countrySearch = original.dataset.country + ' ' + original.dataset.currency;
        var button = document.createElement('button');
        button.type = 'button';
        button.className = 'dac-country__option';
        button.dataset.code = link.dataset.value;
        if (link.getAttribute('aria-current') === 'true') {
          button.classList.add('is-current'); button.setAttribute('aria-current', 'true');
        }
        var flag = link.querySelector('img').cloneNode(true);
        flag.className = 'dac-country__flag'; flag.alt = '';
        var name = document.createElement('span'); name.textContent = original.dataset.country;
        var currency = document.createElement('small'); currency.textContent = original.dataset.currency;
        button.append(flag, name, currency); row.append(button); fragment.append(row);
      });
      options.replaceChildren(fragment);
      rows = Array.from(options.children);
    };
    var previousScroll = 0;
    var filter = function () {
      ensureCountryRows();
      if (!rows) return;
      var query = search.value.trim().toLocaleLowerCase();
      var count = 0;
      rows.forEach(function (row) {
        row.hidden = !row.dataset.countrySearch.toLocaleLowerCase().includes(query);
        if (!row.hidden) count++;
      });
      clear.hidden = !search.value;
      var status = list.querySelector('[data-mobile-country-count]');
      status.textContent = count + ' ' + (count === 1 ? status.dataset.one : status.dataset.many);
      list.querySelector('.dac-country__empty').hidden = count !== 0;
      list.querySelector('.dac-country__options').scrollTop = 0;
    };
    var close = function (returnFocus) {
      if (list.hidden) return;
      list.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      if (drawer) drawer.scrollTop = previousScroll;
      if (returnFocus !== false) toggle.focus({ preventScroll: true });
    };
    form.__dacCloseCountry = close;
    toggle.addEventListener('click', function () {
      if (!list.hidden) { close(); return; }
      previousScroll = drawer ? drawer.scrollTop : 0;
      search.value = '';
      filter();
      list.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      if (drawer) drawer.scrollTop = 0;
      // Keep search opt-in so touch visitors can browse without opening a keyboard.
      list.querySelector('.dac-country__close').focus({ preventScroll: true });
    });
    list.querySelector('.dac-country__close').addEventListener('click', function () { close(); });
    search.addEventListener('input', filter);
    clear.addEventListener('click', function () { search.value = ''; filter(); search.focus({ preventScroll: true }); });
    list.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      close();
    });
    list.addEventListener('click', function (event) {
      var option = event.target.closest('.dac-country__option');
      if (!option) return;
      codeInput.value = option.getAttribute('data-code');
      close(false);
      var submitCountry = function () { form.submit(); };
      var request = new CustomEvent('dac:country-welcome', {
        cancelable: true,
        detail: { countryCode: codeInput.value, onDismiss: submitCountry, onUnavailable: submitCountry }
      });
      if (!document.dispatchEvent(request)) return;
      form.submit();
    });
  }

  /* --------------------------------- Init --------------------------------- */

  function hydrateMobileCards(root) {
    root.querySelectorAll('[data-dac-menu-card-ref]').forEach(function (placeholder) {
      var id = placeholder.dataset.dacMenuCardRef;
      var source = Array.from(document.querySelectorAll('.dac-nav [data-dac-menu-card-source]')).find(function (card) { return card.dataset.dacMenuCardSource === id; });
      if (!source) return;
      var card = source.cloneNode(true);
      card.removeAttribute('data-dac-menu-card-source');
      placeholder.replaceWith(card);
    });
  }

  function init() {
    var nav = document.querySelector(".dac-nav");
    if (nav && !nav.__dacBound) {
      nav.__dacBound = true;
      bindDesktopEvents(nav);
    }

    var mnav = document.querySelector(".dac-mnav");
    if (mnav && !mnav.__dacBound) {
      mnav.__dacBound = true;
      var mobileViewport = window.matchMedia('(max-width: 1279px)');
      var hydrate = function () { if (mobileViewport.matches) hydrateMobileCards(mnav); };
      hydrate();
      mobileViewport.addEventListener('change', hydrate);
      bindMobileEvents(mnav);
      watchDrawerClose(mnav);
    }

    var countryForm = document.querySelector(".dac-country .dac-country__form");
    if (countryForm && !countryForm.__dacBound) {
      countryForm.__dacBound = true;
      bindCountrySelector(countryForm);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

